import {
  Icon,
  SliderModal,
  SliderModalTypes,
  Text,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box, Button, Pressable, ScrollView } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { Dimensions } from 'react-native';
import { RecoilState, useRecoilState } from 'recoil';

import { getEmptySelections, getInitialRangeErrors } from './constants';
import { Autocomplete, AutocompleteTypes } from './filters/Autocomplete';
import { BooleanFilter, BooleanTypes } from './filters/Boolean';
import { Date, DateTypes } from './filters/Date';
import { DateRange, DateRangeTypes } from './filters/DateRange';
import { MultiSlider, MultiSliderTypes } from './filters/MultiSlider';
import { Range, RangeTypes } from './filters/Range';
import { Section, SectionTypes } from './filters/Section';
import { Select, SelectTypes } from './filters/Select';
import { Tags, TagsTypes } from './filters/Tags';
import { filterHasSelection } from './handlers/filterHasSelection';

export declare namespace FilterModalTypes {
  export type FilterType =
    | Omit<AutocompleteTypes.Props, 'onChange' | 'filterIndex'>
    | Omit<MultiSliderTypes.Props, 'onValueChange' | 'filterIndex'>
    | Omit<SectionTypes.Props, 'onSelectionChange' | 'filterIndex'>
    | Omit<TagsTypes.Props, 'onSelectionChange' | 'filterIndex'>
    | Omit<SelectTypes.Props, 'onSelectionChange' | 'filterIndex'>
    | Omit<BooleanTypes.Props, 'onSelectionChange' | 'filterIndex'>
    | Omit<DateTypes.Props, 'onValueChange' | 'filterIndex' | 'zIndex'>
    | Omit<
        DateRangeTypes.Props,
        'onValueChange' | 'onValidationChange' | 'filterIndex' | 'zIndex' | 'selectedFilters'
      >
    | Omit<RangeTypes.Props, 'onValueChange' | 'onErrorChange' | 'filterIndex'>;

  interface FilterAtom {
    availableFilters: FilterModalTypes.FilterType[];
    selectedFilters: FilterModalTypes.SelectedFiltersProps[];
    appliedFilters: FilterModalTypes.SelectedFiltersProps[];
  }

  export interface Props {
    onClose: () => void;
    filterAtom: RecoilState<FilterAtom>;
    modalInsideModal?: SliderModalTypes.Props['modalInsideModal'];
    /**
     * Width of the modal
     * @default: 0.3
     */
    sliderModalWidth?: number;
  }

  export interface SelectedFiltersProps {
    fieldName: string;
    filter:
      | AutocompleteTypes.SelectionProps
      | SectionTypes.SelectionProps
      | TagsTypes.SelectionProps
      | SelectTypes.SelectionProps
      | BooleanTypes.SelectionProps
      | (MultiSliderTypes.RangeProps | object)
      | (RangeTypes.RangeProps | object)
      | DateTypes.SelectionProps
      | DateRangeTypes.SelectionProps;
  }
}

const maxH = Dimensions.get('screen').height * 0.5;

export const FilterModal = ({
  onClose,
  filterAtom,
  sliderModalWidth = 0.3,
  modalInsideModal = false,
}: FilterModalTypes.Props) => {
  const direction = useBreakpointValue<SliderModalTypes.Props['direction']>({
    base: 'bottom',
    lg: 'right',
  });

  const [clearingFilters, setClearingFilters] = useState(false);
  const [filtersAreValid, setFiltersAreValid] = useState<boolean[]>([]);
  const [{ selectedFilters, availableFilters, appliedFilters }, setFiltersState] =
    useRecoilState(filterAtom);

  const directionIsBottom = direction === 'bottom';

  const [rangeErrors, setRangeErrors] = useState(getInitialRangeErrors(availableFilters));

  const hasRangeError = useMemo(() => rangeErrors.some(({ error }) => error), [rangeErrors]);

  const filterSelectionSwitch = ({ filter }, fIndex: number) =>
    filterHasSelection(availableFilters[fIndex].type, filter, availableFilters[fIndex]);

  const hasSelection = selectedFilters.filter((filter) => !!filter).some(filterSelectionSwitch);

  const handleClearFilters = (rerenderFilters?: boolean) => {
    setFiltersAreValid([]);

    if (rerenderFilters) {
      setClearingFilters(true);
    }

    setFiltersState((prevState) => ({
      ...prevState,
      selectedFilters: getEmptySelections(availableFilters),
      appliedFilters: [],
    }));
  };

  const handleResetFiltersSelections = () => {
    const emptySelections = getEmptySelections(availableFilters);
    const newSelectedFilters = emptySelections.map((filter) => {
      const filterApplied = appliedFilters.find(
        (appliedFilter) => appliedFilter.fieldName === filter.fieldName
      );

      return filterApplied || filter;
    });

    setFiltersState((prevState) => ({
      ...prevState,
      selectedFilters: newSelectedFilters,
    }));
  };

  const handleClose = () => {
    handleResetFiltersSelections();
    onClose();
  };

  const handleApplyFilters = () => {
    if (!hasSelection) {
      return setFiltersState((prevState) => ({
        ...prevState,
        selectedFilters: getEmptySelections(availableFilters),
        appliedFilters: [],
      }));
    }
    const filtersWithSelection = selectedFilters.filter(filterSelectionSwitch);
    setFiltersState((prevState) => ({ ...prevState, appliedFilters: filtersWithSelection }));
    onClose();
  };

  const changeSelections = (value: any, fieldName: string, filterIndex: number) => {
    const selections = [...selectedFilters];
    selections[filterIndex] = { fieldName, filter: value };
    setFiltersState((prevState) => ({ ...prevState, selectedFilters: selections }));
  };

  const handleAutocompleteFilterChange: AutocompleteTypes.SelectionChange = (
    value,
    fieldName,
    index
  ) => {
    changeSelections(value, fieldName, index);
  };

  const handleSectionFilterChange: SectionTypes.SelectionChange = (value, fieldName, index) => {
    changeSelections(value, fieldName, index);
  };

  const handleTagsFilterChange: TagsTypes.SelectionChange = (value, fieldName, index) => {
    changeSelections(value, fieldName, index);
  };

  const handleSelectFilterChange: SelectTypes.SelectionChange = (value, fieldName, index) => {
    changeSelections(value, fieldName, index);
  };

  const handleBooleanFilterChange: BooleanTypes.SelectionChange = (value, fieldName, index) => {
    changeSelections(value, fieldName, index);
  };

  const handleDateFilterChange: DateTypes.ValueChange = (value, fieldName, index) => {
    changeSelections(value, fieldName, index);
  };

  const handleDateRangeFilterChange: DateRangeTypes.ValueChange = (value, fieldName, index) => {
    changeSelections(value, fieldName, index);
  };

  const handleMultiSliderFilterChange: MultiSliderTypes.ValueChange = (value, fieldName, index) => {
    changeSelections(value, fieldName, index);
  };

  const handleRangeFilterChange: RangeTypes.ValueChange = (value, fieldName, index) => {
    changeSelections(value, fieldName, index);
  };

  const handleRangeFilterErrorChange = (error: boolean, fieldName: string) => {
    setRangeErrors((prev) =>
      prev.map((rangeError) =>
        rangeError.fieldName === fieldName ? { fieldName, error } : rangeError
      )
    );
  };

  const handleValidationChange = (isValid: boolean, filterIndex: number) => {
    setFiltersAreValid((prev) => {
      const newValidations = [...prev];
      newValidations[filterIndex] = isValid;
      return newValidations;
    });
  };

  const isAllFiltersValid = useMemo(
    () =>
      filtersAreValid
        .filter((item) => item !== null && item !== undefined)
        .every((isValid) => isValid),
    [filtersAreValid]
  );

  const renderFilters = () => {
    if (clearingFilters || !selectedFilters.length) {
      return <></>;
    }

    return availableFilters.map((filter, fIndex) => {
      switch (filter.type) {
        case 'autocomplete':
          return (
            <Autocomplete
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onChange={handleAutocompleteFilterChange}
              selection={selectedFilters[fIndex]?.filter as AutocompleteTypes.SelectionProps}
            />
          );

        case 'multiSlider':
          return (
            <MultiSlider
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onValueChange={handleMultiSliderFilterChange}
              selection={selectedFilters[fIndex]?.filter as MultiSliderTypes.RangeProps}
            />
          );

        case 'section':
          return (
            <Section
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onSelectionChange={handleSectionFilterChange}
              selection={selectedFilters[fIndex]?.filter as SectionTypes.SelectionProps}
            />
          );

        case 'tags':
          return (
            <Tags
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onSelectionChange={handleTagsFilterChange}
              selection={selectedFilters[fIndex]?.filter as TagsTypes.SelectionProps}
            />
          );

        case 'select':
          return (
            <Select
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onSelectionChange={handleSelectFilterChange}
              selection={selectedFilters[fIndex]?.filter as SelectTypes.SelectionProps}
            />
          );

        case 'date':
          return (
            <Date
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onValueChange={handleDateFilterChange}
              zIndex={availableFilters.length - fIndex}
              selection={selectedFilters[fIndex]?.filter as DateTypes.SelectionProps}
            />
          );

        case 'dateRange':
          return (
            <DateRange
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onValueChange={handleDateRangeFilterChange}
              onValidationChange={(isValid) => handleValidationChange(isValid, fIndex)}
              zIndex={availableFilters.length - fIndex}
              selection={selectedFilters[fIndex]?.filter as DateRangeTypes.SelectionProps}
              selectedFilters={selectedFilters}
            />
          );

        case 'range':
          return (
            <Range
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onValueChange={handleRangeFilterChange}
              onErrorChange={handleRangeFilterErrorChange}
              selection={selectedFilters[fIndex]?.filter as RangeTypes.RangeProps}
            />
          );

        case 'boolean':
          return (
            <BooleanFilter
              key={filter.title}
              {...filter}
              filterIndex={fIndex}
              onSelectionChange={handleBooleanFilterChange}
              selection={selectedFilters[fIndex]?.filter as SectionTypes.SelectionProps}
            />
          );

        default:
          return null;
      }
    });
  };

  useEffect(() => {
    if (clearingFilters) {
      setClearingFilters(false);
      setRangeErrors(getInitialRangeErrors(availableFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearingFilters]);

  useEffect(() => {
    if (!selectedFilters.length) {
      handleClearFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SliderModal
      dismissWhenClickOutside
      onDismiss={handleClose}
      direction={direction}
      width={sliderModalWidth}
      modalInsideModal={modalInsideModal}>
      <VStack
        w="full"
        py={Spacing['inline.xxxs']}
        borderTopRadius={{ base: Spacing['inline.xxxs'], lg: 0 }}
        backgroundColor={Color['neutral.white']}
        space={Spacing['inline.xxxs']}>
        {directionIsBottom && (
          <Box
            w="55px"
            alignSelf="center"
            background={'neutral.lightest'}
            borderRadius={BoxRadius.sm}
            h={1}
          />
        )}
        <HStack
          borderTopRadius={{ base: BoxRadius.md, lg: 0 }}
          bg="neutral.white"
          px={Spacing['inline.xxs']}
          space={Spacing['stack.quarck']}
          alignItems="center"
          justifyContent={'space-between'}>
          <Text
            variant="bold"
            fontSize={Typography['fontsize.lg']}
            lineHeight="distant"
            color="neutral.black">
            Escolha os filtros de pesquisa:
          </Text>
          <Pressable testID={'button-close-filter-screen'} onPress={handleClose}>
            <Icon
              testID={'icon-close-filter-screen'}
              name="Close"
              size="xs"
              variant="rounded"
              {...(direction !== 'bottom' && { borderWidth: 0.05 })}
              backgroundColor="transparent"
              color={Color['brand.primary.principal']}
              borderColor={Color['brand.primary.principal']}
            />
          </Pressable>
        </HStack>
      </VStack>

      <ScrollView showsVerticalScrollIndicator={false} {...(directionIsBottom && { maxH })}>
        <VStack
          backgroundColor={Color['neutral.white']}
          px={Spacing['inline.xxs']}
          pb={Spacing['inline.xxs']}
          space={Spacing['stack.xxxs']}>
          {renderFilters()}
        </VStack>
      </ScrollView>
      <Stack
        direction={{ base: 'column', lg: 'row' }}
        bg="neutral.white"
        alignItems={'center'}
        justifyContent={'space-between'}
        p={Spacing['inline.xxs']}
        borderTopWidth={1}
        w={'full'}
        borderTopColor={Color['neutral.lightest']}>
        <Button
          onPress={() => handleClearFilters(true)}
          variant={'textLink'}
          w={{ base: 'full', lg: 'container' }}
          isDisabled={!hasSelection}
          _text={{
            color: hasSelection ? 'feedback.error.default' : 'neutral.light',
            fontFamily: Typography['fontfamily.base.semibold'],
          }}
          rightIcon={
            <Icon
              testID="testID"
              name="Trash"
              color={Color[hasSelection ? 'feedback.error.default' : 'neutral.light']}
              size="xxs"
            />
          }>
          {directionIsBottom ? 'remover filtros' : 'Limpar filtros'}
        </Button>
        <Button
          w={{ base: 'full', lg: 'container' }}
          variant="primary"
          onPress={handleApplyFilters}
          isDisabled={hasRangeError || !isAllFiltersValid}>
          Aplicar filtros
        </Button>
      </Stack>
    </SliderModal>
  );
};
